import { Link } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";

const API_BASE_URL = "https://bluewaterdubai.websiteseoonline.com";
const BLOG_CATEGORY = "Latest News";
const PAGE_SIZE = 9;
const FALLBACK_IMAGE = "/assets/img/blog/blog_1_1.jpg";

function buildApiUrl(pathname) {
  const baseUrl = (process.env.REACT_APP_API_BASE_URL || API_BASE_URL).replace(/\/$/, "");
  return baseUrl + pathname;
}

function imageWithVariant(image, variant, fallback = FALLBACK_IMAGE) {
  const value = String(image || "").trim();

  if (!value) {
    return fallback;
  }

  const normalized = value.startsWith("assets/") ? "/" + value : value;

  if (normalized.includes("imagedelivery.net") && !normalized.includes("/w=")) {
    return normalized.replace(/\/$/, "") + "/" + variant;
  }

  return normalized;
}

function slugify(value) {
  return String(value || "")
    .toLowerCase()
    .trim()
    .replace(/&/g, " and ")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

function blogUrl(blog) {
  if (blog.url) {
    return blog.url;
  }

  return blog.id ? "/blog/" + (slugify(blog.title) || "insight") + "/" + encodeURIComponent(blog.id) : "/blog";
}

function blogDate(blog) {
  return String(blog.date || "").trim();
}

function paginationItems(currentPage, totalPages) {
  const start = Math.max(currentPage - 2, 1);
  const end = Math.min(start + 4, totalPages);
  const adjustedStart = Math.max(end - 4, 1);
  const pages = [];

  for (let page = adjustedStart; page <= end; page += 1) {
    pages.push(page);
  }

  return pages;
}

function BlogCard({ blog }) {
  const detailUrl = blogUrl(blog);

  return (
    <div className="col-lg-4 col-md-6">
      <div className="blog-card">
        <div className="blog-img">
          <Link to={detailUrl}>
            <img src={imageWithVariant(blog.Image, "w=520,sharpen=1")} alt={blog.title || "Insight"} />
          </Link>
          <div className="date">
            <Link to="/blog">{blogDate(blog)}</Link>
          </div>
        </div>
        <div className="blog-content">
          <div className="blog-meta">
            <Link to="/blog">
              <i className="fa-solid fa-user" />
              Blue Water
            </Link>
            <Link to="/blog">
              <i className="fa-solid fa-comments" />
              Insights
            </Link>
          </div>
          <h3 className="box-title">
            <Link to={detailUrl}>{blog.title || "Latest Insight"}</Link>
          </h3>
          {blog.shortdescription ? <p className="blog-excerpt">{blog.shortdescription}</p> : null}
          <Link to={detailUrl} className="th-btn pill style3">
            Read More
          </Link>
        </div>
      </div>
    </div>
  );
}

function Blog() {
  const [blogs, setBlogs] = useState([]);
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState("loading");

  useEffect(() => {
    const controller = new AbortController();

    async function loadBlogs() {
      setStatus("loading");

      try {
        const response = await fetch(buildApiUrl("/api/blogsapi.ashx"), {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            typ: BLOG_CATEGORY,
            id: "0",
          }),
          signal: controller.signal,
        });

        if (!response.ok) {
          throw new Error("Unable to load insights");
        }

        const payload = await response.json();
        setBlogs(payload.result === 1 && Array.isArray(payload.data) ? payload.data : []);
        setPage(1);
        setStatus("ready");
      } catch (err) {
        if (err.name !== "AbortError") {
          setBlogs([]);
          setStatus("error");
        }
      }
    }

    loadBlogs();

    return () => controller.abort();
  }, []);

  const totalPages = Math.max(Math.ceil(blogs.length / PAGE_SIZE), 1);
  const visibleBlogs = useMemo(() => blogs.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE), [blogs, page]);

  return (
    <>
      <div className="breadcumb-wrapper" style={{ backgroundImage: "url('/assets/img/rgew.jpg')" }}>
        <div className="container">
          <div className="breadcumb-content">
            <h1 className="breadcumb-title">Blog</h1>
            <ul className="breadcumb-menu">
              <li>
                <Link to="/">Home</Link>
              </li>
              <li>Blog</li>
            </ul>
          </div>
        </div>
      </div>

      <section className="th-blog-wrapper space-top space-extra-bottom">
        <div className="container">
          {status === "loading" && <div className="text-center">Loading insights...</div>}
          {status === "error" && <div className="text-center">Unable to load insights.</div>}
          {status === "ready" && blogs.length === 0 && <div className="text-center">No insights found.</div>}
          {status === "ready" && blogs.length > 0 && (
            <div className="row gy-30 justify-content-center">
              {visibleBlogs.map((blog) => (
                <BlogCard blog={blog} key={blog.id || blog.title} />
              ))}
              {totalPages > 1 && (
                <div className="th-pagination text-center pt-4">
                  <ul>
                    <li>
                      <button type="button" disabled={page === 1} onClick={() => setPage((currentPage) => Math.max(currentPage - 1, 1))}>
                        <i className="far fa-arrow-left" />
                      </button>
                    </li>
                    {paginationItems(page, totalPages).map((item) => (
                      <li key={item}>
                        <button type="button" className={item === page ? "active" : ""} onClick={() => setPage(item)}>
                          {item}
                        </button>
                      </li>
                    ))}
                    <li>
                      <button
                        type="button"
                        className="next-page"
                        disabled={page === totalPages}
                        onClick={() => setPage((currentPage) => Math.min(currentPage + 1, totalPages))}
                      >
                        Next <i className="far fa-arrow-right" />
                      </button>
                    </li>
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      </section>
    </>
  );
}

export default Blog;
